import type { Antworten, Funnel } from "@fbs/funnel-schema";

import { BASIS } from "./basis";
import { dokumentLesen } from "./dokument";

/**
 * Die Verbindung zur API.
 *
 * Bewusst ohne Bibliothek: zwei Anfragen, beide mit fetch. Alles darüber
 * hinaus wäre Gewicht, das jeder Besucher am Handy mitlädt.
 */

export interface GeladenerFunnel {
  funnelId: string;
  versionId: string;
  version: number;
  funnel: Funnel;
}

export class ApiFehler extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiFehler";
  }
}

async function fehlerLesen(antwort: Response): Promise<ApiFehler> {
  try {
    const koerper: unknown = await antwort.json();
    const fehler = (koerper as { error?: { code?: string; message?: string } }).error;
    return new ApiFehler(
      fehler?.message ?? "Unbekannter Fehler",
      fehler?.code ?? "UNBEKANNT",
      antwort.status,
    );
  } catch {
    return new ApiFehler(
      "Der Server hat nicht geantwortet.",
      "KEINE_ANTWORT",
      antwort.status,
    );
  }
}

export async function funnelLaden(slug: string): Promise<GeladenerFunnel> {
  const antwort = await fetch(`${BASIS}/api/public/funnels/${encodeURIComponent(slug)}`, {
    headers: { accept: "application/json" },
  });

  if (!antwort.ok) throw await fehlerLesen(antwort);

  const koerper = (await antwort.json()) as {
    funnel_id: string;
    version_id: string;
    version: number;
    schema: unknown;
  };

  // Siehe dokument.ts: geprüft wird, worauf sich die Engine verlässt, nicht
  // noch einmal das ganze Schema.
  const funnel = dokumentLesen(koerper.schema);
  if (!funnel) {
    throw new ApiFehler(
      "Dieser Funnel ist fehlerhaft aufgebaut.",
      "SCHEMA_UNGUELTIG",
      500,
    );
  }

  return {
    funnelId: koerper.funnel_id,
    versionId: koerper.version_id,
    version: koerper.version,
    funnel,
  };
}

export interface Absendung {
  funnelSlug: string;
  sessionId?: string | undefined;
  antworten: Antworten;
  kontakt: { first_name: string; last_name: string; email: string; phone: string };
  postcode?: string | undefined;
  city?: string | undefined;
  street?: string | undefined;
  consent: { privacy: boolean; marketing?: boolean };
  dauerMs: number;
  honeypot: string;
  /**
   * Wohin die Antworten gehen.
   *
   * "lead" ist eine Kundenanfrage, "bewerbung" die Meldung eines Betriebs,
   * der Partner werden will. Dieselbe Bedienung, verschiedener Zweck: Eine
   * Bewerbung darf nicht in der Tafel landen und niemandem zugeordnet
   * werden.
   */
  ziel?: "lead" | "bewerbung" | undefined;
}

export interface Absendeergebnis {
  leadId: string;
  referenz: string;
}

export async function leadSenden(daten: Absendung): Promise<Absendeergebnis> {
  const pfad = daten.ziel === "bewerbung" ? "bewerbungen" : "leads";

  const antwort = await fetch(`${BASIS}/api/public/${pfad}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      funnel_slug: daten.funnelSlug,
      session_id: daten.sessionId,
      page_url: window.location.href.slice(0, 2000),
      answers: daten.antworten,
      contact: daten.kontakt,
      postcode: daten.postcode,
      city: daten.city,
      street: daten.street,
      consent: daten.consent,
      meta: {
        time_to_complete_ms: daten.dauerMs,
        website: daten.honeypot,
      },
    }),
  });

  if (!antwort.ok) throw await fehlerLesen(antwort);

  const koerper = (await antwort.json()) as { lead_id: string; reference: string };
  return { leadId: koerper.lead_id, referenz: koerper.reference };
}
